import {
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    DateInput,
    SelectInput,
    TextInput,
    required,
    useRecordContext,
} from 'react-admin';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { AntennaInput } from '@domain/antenna/antenna';
import ImageEditorField from '@app/misc/ImageEditorField';
import { isUrl } from '@app/misc/isUrl';
import { isEmail } from '@app/misc/isEmail';
import { Departments } from '@app/misc/departmentMapping';
import { AddressAutofillInputs } from '@app/misc/AddressAutofillInputs';
import { useResource } from '@app/misc/useResource';

export const AntennaInputs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Stack gap={4} p={1}>
            <AntennaNameInputs />
            <Stack gap={4} direction={isMobile ? 'column' : 'row'}>
                <Stack gap={4} flex={1} sx={{ maxWidth: { md: '50%' } }}>
                    <AntennaInfoInputs />
                    <AntennaContextInputs />
                </Stack>
                <Divider
                    orientation={isMobile ? 'horizontal' : 'vertical'}
                    flexItem
                />
                <Stack gap={4} flex={1}>
                    <AntennaAddressInputs />
                </Stack>
            </Stack>
        </Stack>
    );
};

const AntennaNameInputs = () => {
    const record = useRecordContext<AntennaInput>();

    const { data: coverSrc } = useResource(record?.coverId);

    return (
        <Stack gap={2} flex={1} direction="row">
            <ImageEditorField
                source="coverInput"
                height={60}
                emptyText={record?.name?.charAt(0) ?? ''}
                linkPosition="bottom"
                src={coverSrc ?? undefined}
            />

            <TextInput
                source="name"
                validate={required()}
                helperText={false}
                sx={{
                    mt: 0,
                }}
            />
        </Stack>
    );
};

const AntennaInfoInputs = () => {
    return (
        <Stack>
            <Typography variant="h6">General info</Typography>
            <TextInput source="description" multiline helperText={false} />
            <TextInput source="website" helperText={false} validate={isUrl} />
            <TextInput
                source="whatsapp_group_volunteers"
                label="WhatsApp group (volunteers)"
                helperText={false}
                validate={isUrl}
            />
            <TextInput
                source="whatsapp_group_main_volunteers"
                label="WhatsApp group (main volunteers)"
                helperText={false}
                validate={isUrl}
            />
            <TextInput source="email" helperText={false} validate={isEmail} />
            <TextInput source="phone" helperText={false} />
            <DateInput source="creationDate" helperText={false} />
        </Stack>
    );
};

const AntennaContextInputs = () => {
    const { antennaTypes } = useConfigurationContext();
    return (
        <Stack>
            <Typography variant="h6">Context</Typography>
            <SelectInput
                source="type"
                choices={antennaTypes.map(type => ({
                    id: type.value,
                    name: type.label,
                }))}
                helperText={false}
            />
            <SelectInput
                source="departments"
                choices={Departments}
                SelectProps={{
                    multiple: true,
                }}
                parse={value => {
                    if (!Array.isArray(value)) return [];
                    return value;
                }}
                format={value => {
                    if (!Array.isArray(value)) return [];
                    return value;
                }}
                helperText={false}
            />
        </Stack>
    );
};

const AntennaAddressInputs = () => {
    return (
        <Stack>
            <Typography variant="h6">Address</Typography>
            <AddressAutofillInputs />
        </Stack>
    );
};
